import flatMap from 'lodash/flatMap';
import {computed, observable, reaction, toJS} from 'mobx';
import {ZipApi} from '../lib/ZipApi';
import {getApiErrorMessage} from '../lib/ZipApiError';
import {showToasts} from '../lib/toaster';
import {AppVersion} from '../types/AppVersion';
import {AppFeature, AppWorksWith} from '../types/AppConstants';
import {directoryStore} from './DirectoryStore';
import {VendorType} from '../utils/VendorMapper';
import {
  CategoryFilterStrategy,
  FeatureFilterStrategy,
  InstalledFilterStrategy,
  SearchFilterStrategy,
  VendorFilterStrategy,
  WorksWithFilterStrategy
} from '../filters/strategies';
import {filterAppsByFlags} from '../lib/filterAppsByFlags';

export interface AppFilter {
  installed?: boolean;
  category?: string;
  search?: string;
  feature?: AppFeature;
  worksWith?: AppWorksWith;
  builtBy?: VendorType;
}

export interface AppListItem extends AppVersion {
  installed?: boolean;
}

export class AppListStore {
  @observable public loaded = false;
  @observable public allApps: AppVersion[] = [];
  @observable public filter: AppFilter = {};

  private readonly categoryFilter = new CategoryFilterStrategy();
  private readonly featureFilter = new FeatureFilterStrategy();
  private readonly worksWithFilter = new WorksWithFilterStrategy();
  private readonly vendorFilter = new VendorFilterStrategy();
  private readonly installedFilter = new InstalledFilterStrategy();
  private readonly searchFilter = new SearchFilterStrategy();

  constructor() {
    reaction(() => directoryStore.trackerId, () => {
      if (directoryStore.trackerId) {
        this.fetch();
      }
    });
  }

  public async fetch() {
    try {
      this.allApps = (await ZipApi.listApps(directoryStore.trackerId)).results || [];
      const permittedApps = directoryStore.permittedApps;

      // Sort apps: Opal apps first, then by permitted apps within each group
      this.allApps = this.allApps.sort((lhs, rhs) => {
        const lhsHasOpal = lhs.features?.includes(AppFeature.OPAL_TOOL_APP) ?? false;
        const rhsHasOpal = rhs.features?.includes(AppFeature.OPAL_TOOL_APP) ?? false;

        // First priority: Opal apps come before non-Opal apps
        if (lhsHasOpal && !rhsHasOpal) {
          return -1;
        } else if (!lhsHasOpal && rhsHasOpal) {
          return 1;
        }

        // Within same Opal status, sort by permitted apps
        if (permittedApps?.length) {
          const lhsPermitted = permittedApps.includes(lhs.id.app_id);
          const rhsPermitted = permittedApps.includes(rhs.id.app_id);

          if (lhsPermitted && !rhsPermitted) {
            return -1;
          } else if (!lhsPermitted && rhsPermitted) {
            return 1;
          }
        }

        // Same priority, maintain original order
        return 0;
      });

      this.allApps = filterAppsByFlags(this.allApps);
    } catch (error: any) {
      const message = await getApiErrorMessage(error, 'Unable to load apps');
      showToasts([{intent: 'danger', message}]);
    } finally {
      this.loaded = true;
    }
  }

  @computed
  public get apps(): AppListItem[] {
    return this.allApps.filter((app) => {
      return (
        this.categoryFilter.matches(app, this.filter.category) &&
        this.featureFilter.matches(app, this.filter.feature) &&
        this.worksWithFilter.matches(app, this.filter.worksWith) &&
        this.vendorFilter.matches(app, this.filter.builtBy) &&
        this.installedFilter.matches(app, this.filter.installed) &&
        this.searchFilter.matches(app, this.filter.search)
      );
    });
  }

  @computed get categories() {
    const categorySet = new Set(flatMap(this.allApps, (app) => toJS(app.metadata.categories)));
    return Array.from(categorySet).sort((a, b) => a.toLocaleLowerCase().localeCompare(b));
  }

  public clearFilters() {
    this.filter = {};
  }

  public setFilter(filter: AppFilter) {
    this.filter = filter;
  }

  public hasFilters() {
    return !!(
      this.filter.category ||
      this.filter.installed ||
      this.filter.search ||
      this.filter.feature ||
      this.filter.worksWith ||
      this.filter.builtBy
    );
  }
}

export const appListStore = new AppListStore();
